import {
  actorEmail,
  auditListPath,
  auditStamp,
  eventsOf,
  quotaAuditChange,
  tierQuotaHistory,
  toolDefaultAt,
  type AuditEvent,
  type AuditPage,
} from "../../api/mcp";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { Notice, Panel } from "../ui";
import { Reason } from "../write/ReasonText";
import { AuditChangeDetail } from "./AuditChangeDetail";
import { auditReadFailure, auditTargetPath } from "./RecentChanges";

/**
 * "Who changed this tier's caps" — Recent changes on /policy/caps/{tier}.
 *
 * TWO READS, because one cannot answer it. MCP audits a document write as
 * `set_tier_config` on target `<tier>`, but a removed override as
 * `delete_cap` on `<tier>/<tool>` (admin/routes/quotas.py), and the
 * by-target route is an exact match — so removals never appeared (DA-3706).
 * Both reads are documented routes:
 *
 *   GET /admin/audit/quota/<tier>   the tier's whole document history
 *   GET /admin/audit/quota          the domain, newest first; its `<tier>/`
 *                                   rows are this tier's removals
 *
 * A read per tool would be one request per catalog tool, and still miss a
 * tool the catalog has since dropped. The domain list is one request; its
 * limit (200, MCP's maximum) bounds how far back a removal can be found,
 * which for "recent" changes is the right trade.
 *
 * IN WORDS (DA-3705): an action, then one line per changed tool or limit —
 * "get_order: Metered 30/day → Gated" — rather than two raw JSON documents
 * under the API's action name.
 */
export function TierRecentChanges({
  config,
  tier,
  toolDefault,
  limit = 5,
}: {
  config: AppConfig;
  tier: string;
  /** The tier's `tool_default` today, when the page has read it. */
  toolDefault?: number;
  limit?: number;
}) {
  const own = useResource<AuditPage>(config, `/mcp/admin/audit/quota/${auditTargetPath(tier)}`, [tier]);
  const domain = useResource<AuditPage>(config, auditListPath("quota", 7, 200));

  // Quiet while loading, like RecentChanges: this is a footnote, not the page.
  if (own.state.kind === "loading") return null;
  if (own.state.kind !== "ready") {
    return (
      <Notice tone="amber" title="Could not read recent changes">
        {auditReadFailure(own.state)}
      </Notice>
    );
  }

  const domainRows = domain.state.kind === "ready" ? eventsOf(domain.state.data) : [];
  const history = tierQuotaHistory(eventsOf(own.state.data), domainRows, tier);
  const shown = history.slice(0, limit);

  return (
    <div data-recent-changes>
      <Panel className="p-4">
        <h2 className="text-sm font-semibold">Recent changes</h2>

        {/* The tier's own rows still stand; only removals are missing. Said,
            so "no removal here" is never read into the gap. */}
        {domain.state.kind !== "ready" && domain.state.kind !== "loading" && (
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
            Removed overrides could not be read: {auditReadFailure(domain.state)}
          </p>
        )}

        {shown.length === 0 ? (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Nothing recorded. Changes made before the audit log existed do not appear here.
          </p>
        ) : (
          <ul className="mt-2 space-y-3">
            {shown.map((e, i) => (
              <li
                key={`${e.timestamp ?? "none"}-${i}`}
                className="border-b border-slate-950/5 pb-2 last:border-0 dark:border-white/10"
              >
                <ChangeRow event={e} history={history} toolDefault={toolDefault} />
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function ChangeRow({
  event: e,
  history,
  toolDefault,
}: {
  event: AuditEvent;
  history: AuditEvent[];
  toolDefault?: number;
}) {
  const change = quotaAuditChange(e, {
    toolDefaultAtRemoval:
      e.action === "delete_cap" ? toolDefaultAt(history, e.timestamp, toolDefault) : undefined,
  });

  return (
    <>
      {/* auditStamp: the console's one date format, `2026-09-23 17:28:08 UTC`. */}
      <p className="text-xs text-slate-500 dark:text-slate-400">
        {auditStamp(e.timestamp)} · {actorEmail(e.actor)}
      </p>
      {change ? (
        <>
          <p className="mt-0.5 text-sm font-medium text-slate-950 dark:text-white">{change.verb}</p>
          {change.lines.length === 0 ? (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">No value changed.</p>
          ) : (
            <ul className="mt-1 space-y-0.5 text-xs text-slate-700 dark:text-slate-300">
              {change.lines.map((l) => (
                <li key={l.subject} data-change>
                  {l.tool ? <span className="ref">{l.subject}</span> : l.subject}: {l.from} → {l.to}
                </li>
              ))}
            </ul>
          )}
          {e.reason && (
            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
              <Reason text={e.reason} />
            </p>
          )}
        </>
      ) : (
        // An action this console has no words for: shown under its own name
        // rather than dropped.
        <>
          <p className="mt-0.5 text-xs">
            <span className="ref">{e.action}</span>
          </p>
          <AuditChangeDetail compact before={e.before} after={e.after} action={e.action} reason={e.reason} />
        </>
      )}
    </>
  );
}
